type PanelAxisData = { points?: unknown[]; length?: number };
type RoofData = {
  id?: string | number;
  panels?: unknown[];
  horizontal?: number | string | PanelAxisData;
  vertical?: number | string | PanelAxisData;
  azimuth?: number;
  pitch?: number;
  annualAccess?: number;
  solarAccess?: number[];
  solar_access?: number[];
  annualTSRF?: number;
  tsrf?: number[];
  annualTOF?: number;
  tof?: number[];
};
type PanelGroupData = { horizontal?: PanelAxisData; vertical?: PanelAxisData };
type PanelDataEntry = { realPanels?: unknown[]; points3D?: unknown[] };
type SelectionInput = Record<string, unknown[]>;
type StringEntry = { panels?: unknown[]; [key: string]: unknown };
type PanelCountInput = {
  panelData?: (PanelDataEntry | string | null)[];
  panels?: PanelGroupData[];
  design?: RoofData[];
  designPanels?: PanelGroupData[];
};
type ProductionCallbackInput = {
  annualUsage?: unknown;
  annualKwh?: unknown;
  monthlyKwh?: unknown;
  hourlyProduction?: unknown;
  systemSizeKw?: unknown;
  numTotalPanels?: unknown;
  selectedPanelCount?: unknown;
  perRoofCounts?: number[];
  segments?: RoofData[];
  selectedPanelsPerRoof?: SelectionInput;
  productionPerPanel?: unknown[];
  productionPerString?: unknown[];
  pitchAndAzimuthPerRoof?: unknown;
  shadingPerRoof?: unknown;
  strings?: unknown[] | null;
  invertersUsed?: unknown;
  monthlyClippingKwh?: unknown;
  clippingKwh?: unknown;
  dcAcRatio?: unknown;
  panelCount?: unknown;
  mpptCountTotal?: unknown;
  perRoofAnnualKwh?: unknown;
  perRoofMonthlyAc?: unknown;
  perRoofMonthlyPoa?: unknown;
  perRoofClippingKwh?: unknown;
  limitReached?: boolean;
};

const toNumber = (value: unknown, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const normalizeMonthlyValues = (monthly: unknown, fallback = 0): number[] => {
  const values = Array.isArray(monthly)
    ? monthly.map((val) => toNumber(val, 0))
    : [];
  if (values.length >= 12) return values.slice(0, 12);
  return [...values, ...Array(12 - values.length).fill(fallback)].slice(0, 12);
};

const sumValues = (values: unknown): number =>
  Array.isArray(values)
    ? values.reduce<number>((sum, val) => sum + toNumber(val, 0), 0)
    : toNumber(values, 0);

const isBinaryEntry = (value: unknown) =>
  value === 0 || value === 1 || value === true || value === false;

const toPanelCount = (value: unknown) =>
  Math.max(0, Math.trunc(toNumber(value, 0)));

const isValidPanelIndex = (value: number, total: number) =>
  Number.isInteger(value) && value >= 0 && value < total;

const countSelectedPanels = (
  selectedPanelsPerRoof: SelectionInput = {}
): number =>
  Object.values(selectedPanelsPerRoof || {}).reduce<number>(
    (sum, list) =>
      sum + (Array.isArray(list) ? list.filter(Number.isFinite).length : 0),
    0
  );

const buildPitchAndAzimuthPerRoof = (segments: RoofData[] = []) => {
  if (!Array.isArray(segments)) return [];
  return segments.map((segment, idx) => ({
    idx,
    azimuth: segment?.azimuth,
    pitch: segment?.pitch
  }));
};

const buildShadingPerRoof = (segments: RoofData[] = []) => {
  if (!Array.isArray(segments)) return [];
  return segments.map((segment, idx) => ({
    idx,
    SA:
      segment?.annualAccess ??
      segment?.solarAccess?.[12] ??
      segment?.solar_access?.[12] ??
      0,
    TSRF: segment?.annualTSRF ?? segment?.tsrf?.[12] ?? 0,
    TOF: segment?.annualTOF ?? segment?.tof?.[12] ?? 0
  }));
};

const getRoofPanelCountFromSegment = (segment: RoofData | undefined) => {
  if (!segment || typeof segment !== 'object') return 0;
  if (Array.isArray(segment?.panels)) return segment.panels.length;

  const horizontal = toNumber(segment?.horizontal, 0);
  const vertical = toNumber(segment?.vertical, 0);
  const directCount = horizontal + vertical;
  if (directCount > 0) return directCount;

  const horizontalPoints = Array.isArray(
    (segment?.horizontal as PanelAxisData | undefined)?.points
  )
    ? (segment.horizontal as PanelAxisData).points!.length
    : 0;
  const verticalPoints = Array.isArray(
    (segment?.vertical as PanelAxisData | undefined)?.points
  )
    ? (segment.vertical as PanelAxisData).points!.length
    : 0;
  return horizontalPoints + verticalPoints;
};

const buildFallbackProductionPerPanel = ({
  segments,
  perRoofCounts,
  selectedPanelsPerRoof,
  selectedPanelCount,
  annualKwhValue
}: {
  segments?: RoofData[];
  perRoofCounts?: number[];
  selectedPanelsPerRoof?: SelectionInput;
  selectedPanelCount?: unknown;
  annualKwhValue: number;
}) => {
  const safeSegments = Array.isArray(segments) ? segments : [];
  const safePerRoofCounts = Array.isArray(perRoofCounts) ? perRoofCounts : [];
  const selectedByRoof =
    selectedPanelsPerRoof && typeof selectedPanelsPerRoof === 'object'
      ? selectedPanelsPerRoof
      : {};
  const selectedRoofIndices = Object.keys(selectedByRoof)
    .map((key) => Number(key))
    .filter((idx) => Number.isFinite(idx) && idx >= 0);

  const roofCount = Math.max(
    safeSegments.length,
    safePerRoofCounts.length,
    selectedRoofIndices.length > 0 ? Math.max(...selectedRoofIndices) + 1 : 0
  );
  if (roofCount <= 0) return [];

  const resolvedSelectedPanelCount = Number.isFinite(Number(selectedPanelCount))
    ? Number(selectedPanelCount)
    : countSelectedPanels(selectedByRoof);
  const annualPerSelectedPanel =
    resolvedSelectedPanelCount > 0
      ? annualKwhValue / resolvedSelectedPanelCount
      : 0;

  return Array.from({ length: roofCount }, (_, roofIdx) => {
    const selectedRaw =
      selectedByRoof?.[roofIdx] ?? selectedByRoof?.[String(roofIdx)] ?? [];
    const selectedIndices = Array.isArray(selectedRaw)
      ? selectedRaw
          .map((panelIdx) => Number(panelIdx))
          .filter((panelIdx) => Number.isFinite(panelIdx) && panelIdx >= 0)
      : [];
    const uniqueSelected = [...new Set(selectedIndices)];

    const countFromPayload = Number(safePerRoofCounts?.[roofIdx]);
    const countFromSegment = getRoofPanelCountFromSegment(
      safeSegments?.[roofIdx]
    );
    const countFromSelected =
      uniqueSelected.length > 0 ? Math.max(...uniqueSelected) + 1 : 0;
    const roofPanelCount = Math.max(
      Number.isFinite(countFromPayload) ? countFromPayload : 0,
      countFromSegment,
      countFromSelected
    );

    const roofPanels = Array(Math.max(roofPanelCount, 0)).fill(0);
    uniqueSelected.forEach((panelIdx) => {
      if (panelIdx >= 0 && panelIdx < roofPanels.length) {
        roofPanels[panelIdx] = annualPerSelectedPanel;
      }
    });

    return {
      idx: roofIdx,
      roofPanels
    };
  });
};

export const getPanelCounts = ({
  panelData,
  panels,
  design,
  designPanels
}: PanelCountInput = {}) => {
  if (Array.isArray(panelData) && panelData.length > 0) {
    const perRoofCounts = panelData.map((panelEntry) => {
      try {
        const parsed =
          typeof panelEntry === 'string' ? JSON.parse(panelEntry) : panelEntry;
        const realPanels = Array.isArray(parsed?.realPanels)
          ? parsed.realPanels
          : null;
        if (realPanels) return realPanels.filter(Boolean).length;
        const points3D = Array.isArray(parsed?.points3D) ? parsed.points3D : [];
        return points3D.length;
      } catch (e) {
        return 0;
      }
    });
    const totalCount = perRoofCounts.reduce((sum, count) => sum + count, 0);
    return { totalCount, perRoofCounts };
  }

  if (Array.isArray(panels) && panels.length > 0) {
    const perRoofCounts = panels.map((panel) => {
      const horizontalCount =
        panel?.horizontal?.points?.length ?? panel?.horizontal?.length ?? 0;
      const verticalCount =
        panel?.vertical?.points?.length ?? panel?.vertical?.length ?? 0;
      return horizontalCount + verticalCount;
    });
    const totalCount = perRoofCounts.reduce((sum, count) => sum + count, 0);
    return { totalCount, perRoofCounts };
  }

  if (Array.isArray(design) && design.length > 0) {
    const perRoofCounts = design.map((roof) => {
      if (Array.isArray(roof?.panels)) return roof.panels.length;
      const horizontal = toNumber(roof?.horizontal, 0);
      const vertical = toNumber(roof?.vertical, 0);
      return horizontal + vertical;
    });
    const totalCount = perRoofCounts.reduce((sum, count) => sum + count, 0);
    if (totalCount > 0) return { totalCount, perRoofCounts };
  }

  if (Array.isArray(designPanels) && designPanels.length > 0) {
    const perRoofCounts = designPanels.map((panel) => {
      const horizontalCount =
        panel?.horizontal?.points?.length ?? panel?.horizontal?.length ?? 0;
      const verticalCount =
        panel?.vertical?.points?.length ?? panel?.vertical?.length ?? 0;
      return horizontalCount + verticalCount;
    });
    const totalCount = perRoofCounts.reduce((sum, count) => sum + count, 0);
    return { totalCount, perRoofCounts };
  }

  return { totalCount: 0, perRoofCounts: [] };
};

export const mapSelectedPanelsPerRoof = (
  selectedPanelsPerRoof: SelectionInput = {},
  panelIndexMap: unknown[][] | null = null
) => {
  const mapped: Record<number, number[]> = {};
  const mapByRoof = Array.isArray(panelIndexMap) ? panelIndexMap : null;

  Object.keys(selectedPanelsPerRoof || {}).forEach((roofKey) => {
    const roofIdx = Number(roofKey);
    if (!Number.isFinite(roofIdx)) return;
    const raw = selectedPanelsPerRoof[roofKey];
    const list = Array.isArray(raw) ? raw : [];
    const mappedList = list
      .map((panelIdx) => {
        const idx = Number(panelIdx);
        if (!Number.isFinite(idx)) return null;
        if (mapByRoof && Array.isArray(mapByRoof[roofIdx])) {
          const actualIdx = mapByRoof[roofIdx][idx];
          const mappedIdx = Number(actualIdx);
          return Number.isFinite(mappedIdx) ? mappedIdx : null;
        }
        return idx;
      })
      .filter((val): val is number => val !== null && Number.isFinite(val));
    mapped[roofIdx] = mappedList;
  });

  return mapped;
};

export const resolveRoofIndexFromPayload = (
  roofIdxRaw: unknown,
  roofIndexMap: unknown[] | null = null
) => {
  const roofIdx = Number(roofIdxRaw);
  if (!Number.isFinite(roofIdx)) return null;

  if (Array.isArray(roofIndexMap)) {
    const mappedIdx = Number(roofIndexMap[roofIdx]);
    if (Number.isFinite(mappedIdx)) return mappedIdx;
  }

  return roofIdx;
};

export const mapRoofKeyedPayloadToDesign = (
  payload: Record<string, unknown> | unknown[] = {},
  roofIndexMap: unknown[] | null = null
) => {
  if (!payload || typeof payload !== 'object') return {};

  const mapped: Record<number, unknown> | unknown[] = Array.isArray(payload)
    ? []
    : {};
  Object.keys(payload).forEach((roofKey) => {
    const designRoofIdx = resolveRoofIndexFromPayload(roofKey, roofIndexMap);
    if (designRoofIdx === null || !Number.isFinite(designRoofIdx)) return;
    mapped[designRoofIdx] = (payload as Record<string, unknown>)[roofKey];
  });

  return mapped;
};

export const mapSelectedPanelsPerRoofToDesign = (
  selectedPanelsPerRoof: SelectionInput = {},
  roofIndexMap: unknown[] | null = null,
  panelIndexMap: unknown[][] | null = null
) => {
  const mappedByPanel = mapSelectedPanelsPerRoof(
    selectedPanelsPerRoof,
    panelIndexMap
  );

  return Object.keys(mappedByPanel).reduce<Record<number, number[]>>(
    (acc, roofKey) => {
      const designRoofIdx = resolveRoofIndexFromPayload(roofKey, roofIndexMap);
      if (designRoofIdx === null || !Number.isFinite(designRoofIdx)) return acc;
      acc[designRoofIdx] = mappedByPanel[Number(roofKey)];
      return acc;
    },
    {}
  );
};

export function normalizeProductionPerPanelData({
  raw,
  perRoofCounts,
  roofIndexMap,
  panelIndexMap
}: {
  raw?: any[];
  perRoofCounts?: number[];
  roofIndexMap?: number[] | null;
  panelIndexMap?: number[][] | null;
} = {}) {
  if (!Array.isArray(raw)) return [];
  const counts = Array.isArray(perRoofCounts) ? perRoofCounts : [];
  if (counts.length === 0) return raw;
  const output = counts.map((count, index) => ({
    idx: index,
    roofPanels: Array(Number(count) || 0).fill(0)
  }));
  const roofMap = Array.isArray(roofIndexMap) ? roofIndexMap : null;
  raw.forEach((entry, rawIndex) => {
    const payloadRoofIndex = Number(entry?.idx ?? rawIndex);
    if (!Number.isFinite(payloadRoofIndex)) return;
    const segmentIndex =
      roofMap && Number.isFinite(Number(roofMap[payloadRoofIndex]))
        ? Number(roofMap[payloadRoofIndex])
        : payloadRoofIndex;
    const roof = output[segmentIndex];
    if (!roof || !Array.isArray(roof.roofPanels)) return;
    const values = Array.isArray(entry?.roofPanels) ? entry.roofPanels : [];
    const map = panelIndexMap?.[payloadRoofIndex];
    values.forEach((value: unknown, panelIndex: number) => {
      const targetIndex = Array.isArray(map) ? map[panelIndex] : panelIndex;
      const normalizedIndex = Number(targetIndex);
      if (
        !Number.isFinite(normalizedIndex) ||
        normalizedIndex < 0 ||
        normalizedIndex >= roof.roofPanels.length
      ) {
        return;
      }
      roof.roofPanels[normalizedIndex] = Number(value) || 0;
    });
  });
  return output;
}

export const mapStringingDataToDesign = (
  strings: StringEntry[] | null = null,
  roofIndexMap: unknown[] | null = null,
  panelIndexMap: unknown[][] | null = null
) => {
  if (!Array.isArray(strings)) return strings;

  return strings.map((entry) => {
    const panels = Array.isArray(entry?.panels) ? entry.panels : [];
    const mappedPanels = panels
      .map((panel) => {
        if (!Array.isArray(panel) || panel.length < 2) return null;

        const payloadRoofIdx = Number(panel[0]);
        const payloadPanelIdx = Number(panel[1]);
        if (
          !Number.isFinite(payloadRoofIdx) ||
          !Number.isFinite(payloadPanelIdx)
        ) {
          return null;
        }

        const designRoofIdx = resolveRoofIndexFromPayload(
          payloadRoofIdx,
          roofIndexMap
        );
        if (designRoofIdx === null || !Number.isFinite(designRoofIdx))
          return null;

        const roofPanelIndexMap = Array.isArray(panelIndexMap)
          ? panelIndexMap[payloadRoofIdx]
          : null;
        const actualPanelIdx = Array.isArray(roofPanelIndexMap)
          ? Number(roofPanelIndexMap[payloadPanelIdx])
          : payloadPanelIdx;
        if (!Number.isFinite(actualPanelIdx)) return null;

        return [designRoofIdx, actualPanelIdx];
      })
      .filter(Boolean);

    return {
      ...entry,
      panels: mappedPanels
    };
  });
};

export function stringingUsesCanvasRoofs(
  strings: unknown,
  selectedPerRoof: Record<number, number[]>
): boolean {
  const selectedRoofs = new Set(
    Object.keys(selectedPerRoof || {})
      .map(Number)
      .filter(Number.isFinite)
  );
  if (selectedRoofs.size === 0) return false;
  if (!Array.isArray(strings) || strings.length === 0) return false;
  const stringRoofs = new Set<number>();
  strings.forEach((entry) => {
    const panels = Array.isArray((entry as { panels?: unknown })?.panels)
      ? ((entry as { panels: unknown[] }).panels as unknown[])
      : [];
    panels.forEach((pair) => {
      if (!Array.isArray(pair) || pair.length < 2) return;
      const roof = Number(pair[0]);
      if (Number.isFinite(roof)) stringRoofs.add(roof);
    });
  });
  if (stringRoofs.size === 0) return false;
  for (const roof of stringRoofs) {
    if (!selectedRoofs.has(roof)) return false;
  }
  return true;
}

export const getProductionPerPanelResult = (
  result: any = {},
  fallback: any = []
) =>
  result?.production_per_panel ??
  result?.productionPerPanel ??
  result?.production_results?.production_per_panel ??
  fallback;

export const getProductionPerStringResult = (result: any = {}) => {
  const value =
    result?.production_per_string ??
    result?.productionPerString ??
    result?.production_results?.production_per_string ??
    [];
  return Array.isArray(value) ? value : [];
};

export const buildStringingIndexMapsFromPanelData = (
  panelDataAll: any[] = []
) => {
  if (!Array.isArray(panelDataAll) || panelDataAll.length === 0) {
    return { roofIndexMap: null, selectedPanelIndexMap: null };
  }

  const roofIndexMap: number[] = [];
  const selectedPanelIndexMap: number[][] = [];

  try {
    panelDataAll.forEach((panelData, segmentIndex) => {
      if (!panelData) return;
      const parsed =
        typeof panelData === 'string' ? JSON.parse(panelData) : panelData;
      const points3D = parsed?.points3D || [];
      if (!Array.isArray(points3D) || points3D.length === 0) return;

      const realPanels = parsed?.realPanels || null;
      const hasRealPanels =
        (Array.isArray(realPanels) && realPanels.length > 0) ||
        Boolean(realPanels && typeof realPanels === 'object');
      const panelIndices = points3D
        .map((_: unknown, panelIndex: number) => panelIndex)
        .filter((panelIndex: number) =>
          hasRealPanels ? Boolean(realPanels?.[panelIndex]) : true
        );
      if (panelIndices.length === 0) return;

      const payloadRoofIndex = roofIndexMap.length;
      roofIndexMap.push(segmentIndex);
      selectedPanelIndexMap[payloadRoofIndex] = panelIndices;
    });
  } catch (error) {
    return { roofIndexMap: null, selectedPanelIndexMap: null };
  }

  return {
    roofIndexMap: roofIndexMap.length > 0 ? roofIndexMap : null,
    selectedPanelIndexMap:
      selectedPanelIndexMap.length > 0 ? selectedPanelIndexMap : null
  };
};

const getSelectedStringingIds = (
  strings: any[],
  mode: 'full' | 'roof' | 'direct',
  roofIndexMap: number[] | null,
  selectedPanelIndexMap: number[][] | null
) => {
  const ids: string[] = [];
  strings.forEach((string) => {
    const panels = Array.isArray(string?.panels) ? string.panels : [];
    panels.forEach((panel: any) => {
      if (!Array.isArray(panel) || panel.length < 2) return;
      const payloadRoofIndex = Number(panel[0]);
      const payloadPanelIndex = Number(panel[1]);
      if (
        !Number.isFinite(payloadRoofIndex) ||
        !Number.isFinite(payloadPanelIndex)
      ) {
        return;
      }

      const segmentIndexValue =
        mode === 'direct'
          ? payloadRoofIndex
          : Array.isArray(roofIndexMap)
            ? roofIndexMap[payloadRoofIndex]
            : payloadRoofIndex;
      const segmentIndex = Number(segmentIndexValue);
      if (!Number.isFinite(segmentIndex)) return;

      const panelMap = Array.isArray(selectedPanelIndexMap)
        ? selectedPanelIndexMap[payloadRoofIndex]
        : null;
      const panelIndexValue =
        mode === 'full'
          ? Array.isArray(panelMap)
            ? panelMap[payloadPanelIndex]
            : null
          : payloadPanelIndex;
      const panelIndex = Number(panelIndexValue);
      if (!Number.isFinite(panelIndex)) return;
      ids.push(`${segmentIndex}-${panelIndex}`);
    });
  });
  return [...new Set(ids)];
};

export const resolveStringingSelectionIds = ({
  strings = [],
  roofIndexMap = null,
  selectedPanelIndexMap = null,
  selectedObjectsPanels = {}
}: {
  strings?: any[];
  roofIndexMap?: number[] | null;
  selectedPanelIndexMap?: number[][] | null;
  selectedObjectsPanels?: Record<string, number[]>;
} = {}) => {
  if (!Array.isArray(strings) || strings.length === 0) return [];

  const expected = new Set<string>();
  Object.keys(selectedObjectsPanels || {}).forEach((segmentKey) => {
    const indices = Array.isArray(selectedObjectsPanels?.[segmentKey])
      ? selectedObjectsPanels[segmentKey]
      : [];
    indices.forEach((panelIndexValue) => {
      const segmentIndex = Number(segmentKey);
      const panelIndex = Number(panelIndexValue);
      if (Number.isFinite(segmentIndex) && Number.isFinite(panelIndex)) {
        expected.add(`${segmentIndex}-${panelIndex}`);
      }
    });
  });

  const candidates = (['full', 'roof', 'direct'] as const).map((mode) => ({
    mode,
    ids: getSelectedStringingIds(
      strings,
      mode,
      roofIndexMap,
      selectedPanelIndexMap
    )
  }));
  if (expected.size === 0) {
    return candidates.find((candidate) => candidate.ids.length > 0)?.ids || [];
  }

  return (
    candidates
      .map((candidate) => ({
        ...candidate,
        overlap: candidate.ids.reduce(
          (sum, id) => sum + (expected.has(id) ? 1 : 0),
          0
        )
      }))
      .sort((a, b) => {
        if (b.overlap !== a.overlap) return b.overlap - a.overlap;
        return b.ids.length - a.ids.length;
      })[0]?.ids || []
  );
};

export const getSelectedPanelsPerRoofFromDesign = (
  design: RoofData[] = [],
  selectedPanels: SelectionInput = {}
) => {
  const mapped: Record<number, number[]> = {};
  if (!Array.isArray(design)) return mapped;

  design.forEach((roof, idx) => {
    const raw = selectedPanels?.[String(roof?.id)] ?? [];
    const list = Array.isArray(raw) ? raw : [];
    const horizontalCount = toPanelCount(roof?.horizontal);
    const verticalCount = toPanelCount(roof?.vertical);
    const total = horizontalCount + verticalCount;
    const looksBinary = list.length === total && list.every(isBinaryEntry);
    const persistedIndices = looksBinary
      ? list
          .map((value, panelIdx) => (value ? panelIdx : null))
          .filter((idx): idx is number => idx !== null && Number.isFinite(idx))
      : list
          .map((value) => Number(value))
          .filter((panelIdx) => isValidPanelIndex(panelIdx, total));
    mapped[idx] = persistedIndices.map((panelIdx) =>
      panelIdx < verticalCount
        ? horizontalCount + panelIdx
        : panelIdx - verticalCount
    );
  });

  return mapped;
};

export const buildSelectedPanelsStateFromCanvasSelection = (
  design: RoofData[] = [],
  selectedPanelsPerRoof: SelectionInput = {},
  previousSelectedPanels: SelectionInput = {}
) => {
  const selectedPanels = { ...(previousSelectedPanels || {}) };

  (Array.isArray(design) ? design : []).forEach((roof, roofIdx) => {
    if (!roof?.id) return;
    const horizontalCount = toPanelCount(roof.horizontal);
    const verticalCount = toPanelCount(roof.vertical);
    const total = horizontalCount + verticalCount;
    const selectedRaw =
      selectedPanelsPerRoof?.[roofIdx] ??
      selectedPanelsPerRoof?.[String(roofIdx)] ??
      [];
    const selectedCanvasIndices = new Set(
      (Array.isArray(selectedRaw) ? selectedRaw : [])
        .map(Number)
        .filter((panelIdx) => isValidPanelIndex(panelIdx, total))
    );
    const persisted = Array(total).fill(0);

    selectedCanvasIndices.forEach((panelIdx) => {
      const persistedIdx =
        panelIdx < horizontalCount
          ? verticalCount + panelIdx
          : panelIdx - horizontalCount;
      if (persistedIdx >= 0 && persistedIdx < total) {
        persisted[persistedIdx] = 1;
      }
    });

    selectedPanels[roof.id] = persisted;
  });

  return selectedPanels;
};

export const buildProductionCallbackPayload = ({
  annualUsage,
  annualKwh,
  monthlyKwh,
  hourlyProduction,
  systemSizeKw,
  numTotalPanels,
  selectedPanelCount,
  perRoofCounts,
  segments,
  selectedPanelsPerRoof,
  productionPerPanel,
  productionPerString,
  pitchAndAzimuthPerRoof,
  shadingPerRoof,
  strings,
  invertersUsed,
  monthlyClippingKwh,
  clippingKwh,
  dcAcRatio,
  panelCount,
  mpptCountTotal,
  perRoofAnnualKwh,
  perRoofMonthlyAc,
  perRoofMonthlyPoa,
  perRoofClippingKwh,
  limitReached = false
}: ProductionCallbackInput = {}) => {
  const annualUsageValue = sumValues(annualUsage);
  const annualKwhValue = toNumber(annualKwh, 0);
  const monthlyValues = normalizeMonthlyValues(monthlyKwh, 0);
  const offset =
    annualUsageValue > 0 ? (annualKwhValue / annualUsageValue) * 100 : 0;

  const resolvedSelectedPanelsPerRoof =
    selectedPanelsPerRoof && typeof selectedPanelsPerRoof === 'object'
      ? selectedPanelsPerRoof
      : {};
  const resolvedSelectedPanelCount = Number.isFinite(Number(selectedPanelCount))
    ? Number(selectedPanelCount)
    : countSelectedPanels(resolvedSelectedPanelsPerRoof);
  const resolvedNumTotalPanels = Number.isFinite(Number(numTotalPanels))
    ? Number(numTotalPanels)
    : resolvedSelectedPanelCount;
  const resolvedSystemSizeKw = Number.isFinite(Number(systemSizeKw))
    ? Number(systemSizeKw)
    : null;
  const resolvedHourlyProduction =
    hourlyProduction !== undefined && hourlyProduction !== null
      ? hourlyProduction
      : [];
  const hasExplicitProductionPerPanel =
    Array.isArray(productionPerPanel) && productionPerPanel.length > 0;
  const resolvedProductionPerPanel = hasExplicitProductionPerPanel
    ? productionPerPanel
    : buildFallbackProductionPerPanel({
        segments,
        perRoofCounts,
        selectedPanelsPerRoof: resolvedSelectedPanelsPerRoof,
        selectedPanelCount: resolvedSelectedPanelCount,
        annualKwhValue
      });
  const resolvedProductionPerString = Array.isArray(productionPerString)
    ? productionPerString
    : [];

  const payload = {
    offset: Math.round(offset),
    systemSize:
      resolvedSystemSizeKw !== null
        ? Number(resolvedSystemSizeKw).toFixed(3)
        : '0.000',
    yearlyProduction: annualKwhValue.toFixed(0),
    monthlyProduction: monthlyValues.map((value) => Number(value).toFixed(0)),
    hourlyProduction: resolvedHourlyProduction,
    numTotalPanels: resolvedNumTotalPanels,
    numSelectedPanels: resolvedSelectedPanelCount,
    productionPerPanel: resolvedProductionPerPanel,
    productionPerString: resolvedProductionPerString,
    pitchAndAzimuthPerRoof:
      pitchAndAzimuthPerRoof ?? buildPitchAndAzimuthPerRoof(segments),
    shadingPerRoof: shadingPerRoof ?? buildShadingPerRoof(segments),
    stringingData: strings || null,
    invertersUsed: invertersUsed ?? null,
    stringsCount: Array.isArray(strings) ? strings.length : 0,
    monthlyClippingKwh: normalizeMonthlyValues(monthlyClippingKwh, 0),
    clippingKwh: toNumber(clippingKwh, 0),
    dcAcRatio: dcAcRatio ?? null,
    panelCount: Number.isFinite(Number(panelCount))
      ? Number(panelCount)
      : resolvedSelectedPanelCount,
    selectedPanelsPerRoof: resolvedSelectedPanelsPerRoof,
    systemSizeKw: resolvedSystemSizeKw,
    mpptCountTotal: mpptCountTotal ?? null,
    perRoofAnnualKwh: perRoofAnnualKwh || {},
    perRoofMonthlyAc: perRoofMonthlyAc || {},
    perRoofMonthlyPoa: perRoofMonthlyPoa || {},
    perRoofClippingKwh: perRoofClippingKwh || {},
    limit_reached: Boolean(limitReached)
  };

  return payload;
};

export function selectedPanelsPerRoofToDesignSelection(
  selectedIndices: any,
  verticalCount: any,
  horizontalCount: any
) {
  const set = new Set(
    (Array.isArray(selectedIndices) ? selectedIndices : [])
      .map((n) => Number(n))
      .filter(Number.isFinite)
  );
  const h = Array.from({ length: verticalCount }, (_, j) =>
    set.has(j) ? 1 : 0
  ).join(',');
  const v = Array.from({ length: horizontalCount }, (_, j) =>
    set.has(verticalCount + j) ? 1 : 0
  ).join(',');
  return { vertical_selected: v, horizontal_selected: h };
}
